import posthog from 'posthog-js';
import supabase from '@/lib/supabase';

const UTM_STORAGE_KEY = 'pengi_utm_attribution';
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

interface UTMAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  landing_page?: string;
  referrer?: string;
  timestamp?: string;
}

/**
 * 落地页加载时立即捕获 URL 中的 UTM 参数到 localStorage。
 * 应在 app 初始化早期调用，确保用户跳转到 Chrome Web Store 等外部页面后仍能读取。
 */
export function captureUTMFromURL(): void {
  if (typeof window === 'undefined') return;

  try {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('utm_source');
    if (!source) return;

    const data: Record<string, string> = {
      utm_source: source,
      landing_page: window.location.pathname + window.location.search,
      referrer: document.referrer || '',
      timestamp: new Date().toISOString(),
    };
    for (const key of UTM_PARAMS) {
      const val = params.get(key);
      if (val) data[key] = val;
    }

    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage 不可用时静默失败
  }
}

/**
 * 获取用户的 UTM 归因数据。
 * 优先级：URL 参数 > localStorage > PostHog（兜底）。
 */
export function getUTMAttribution(): UTMAttribution | null {
  if (typeof window === 'undefined') return null;

  try {
    // 1. 直接从当前 URL 读取
    const params = new URLSearchParams(window.location.search);
    const fromURL: UTMAttribution = {
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
      utm_term: params.get('utm_term') || undefined,
      utm_content: params.get('utm_content') || undefined,
      landing_page: window.location.pathname + window.location.search,
      referrer: document.referrer || undefined,
    };
    if (fromURL.utm_source || fromURL.utm_medium || fromURL.utm_campaign) {
      return fromURL;
    }

    // 2. 从 localStorage 读取（用户可能已从落地页跳走，如 Chrome Web Store）
    try {
      const stored = localStorage.getItem(UTM_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as UTMAttribution;
        if (parsed.utm_source || parsed.utm_medium || parsed.utm_campaign) {
          return parsed;
        }
      }
    } catch {
      // 解析失败时跳过
    }

    // 3. PostHog 兜底（用户长时间停留后 PostHog 可能已加载完）
    const attribution: UTMAttribution = {
      utm_source: posthog.get_property('utm_source') || posthog.get_property('$initial_utm_source'),
      utm_medium: posthog.get_property('utm_medium') || posthog.get_property('$initial_utm_medium'),
      utm_campaign: posthog.get_property('utm_campaign') || posthog.get_property('$initial_utm_campaign'),
      utm_term: posthog.get_property('utm_term') || posthog.get_property('$initial_utm_term'),
      utm_content: posthog.get_property('utm_content') || posthog.get_property('$initial_utm_content'),
      referrer: posthog.get_property('$referrer') || posthog.get_property('$initial_referrer'),
      landing_page: window.location.pathname,
    };
    if (attribution.utm_source || attribution.utm_medium || attribution.utm_campaign) {
      return attribution;
    }

    return null;
  } catch (error) {
    console.error('Failed to get UTM attribution:', error);
    return null;
  }
}

/**
 * 获取用户的完整归因信息（包括初始和最近的）
 */
export function getFullAttributionData() {
  if (typeof window === 'undefined') return null;

  try {
    return {
      // 初始归因（首次访问）
      initial: {
        utm_source: posthog.get_property('$initial_utm_source'),
        utm_medium: posthog.get_property('$initial_utm_medium'),
        utm_campaign: posthog.get_property('$initial_utm_campaign'),
        utm_term: posthog.get_property('$initial_utm_term'),
        utm_content: posthog.get_property('$initial_utm_content'),
        referrer: posthog.get_property('$initial_referrer'),
        referring_domain: posthog.get_property('$initial_referring_domain')
      },
      // 当前/最近归因
      current: {
        utm_source: posthog.get_property('utm_source'),
        utm_medium: posthog.get_property('utm_medium'),
        utm_campaign: posthog.get_property('utm_campaign'),
        utm_term: posthog.get_property('utm_term'),
        utm_content: posthog.get_property('utm_content'),
        referrer: posthog.get_property('$referrer'),
        referring_domain: posthog.get_property('$referring_domain')
      }
    };
  } catch (error) {
    console.error('Failed to get full attribution data from PostHog:', error);
    return null;
  }
}

/**
 * 用户注册时保存UTM归因数据到Supabase
 * PostHog 自动处理用户身份关联，我们只需要保存到自己的数据库
 */
export async function saveUTMAttributionOnRegistration(userId: string, userEmail?: string): Promise<void> {
  const attribution = getUTMAttribution();
  
  if (!attribution) {
    console.log('No UTM attribution data to save');
    return;
  }

  try {
    // 保存到 Supabase user_utm_attribution 表
    const { error } = await supabase
      .from('user_utm_attribution')
      .upsert({
        user_id: userId,
        utm_source: attribution.utm_source,
        utm_medium: attribution.utm_medium,
        utm_campaign: attribution.utm_campaign,
        utm_term: attribution.utm_term,
        utm_content: attribution.utm_content,
        landing_page: attribution.landing_page,
        referrer: attribution.referrer,
        attribution_timestamp: new Date().toISOString(),
        created_at: new Date().toISOString()
      }, {
        onConflict: 'user_id' // 每个用户只保存一条记录（首次归因）
      });

    if (error) {
      console.error('Failed to save UTM attribution to Supabase:', error);
      throw error;
    }

    console.log('UTM attribution saved to Supabase for user:', userId);
    
    // PostHog 自动处理用户身份识别，无需手动调用
    // PostHog 会自动关联匿名会话和已识别用户
    
  } catch (error) {
    console.error('Failed to save UTM attribution for user:', error);
  }
}

/**
 * 用户登录时更新身份关联
 * PostHog 自动处理身份关联，我们只需要确保数据同步
 */
export async function updateUTMAttributionOnLogin(userId: string, userEmail?: string): Promise<void> {
  try {
    // PostHog 的 identify() 会自动关联匿名会话和登录用户
    // 检查用户是否已有归因数据，如果没有且当前有UTM数据，则保存
    
    const attribution = getUTMAttribution();
    if (attribution) {
      // 检查用户是否已有归因记录
      const { data: existingAttribution } = await supabase
        .from('user_utm_attribution')
        .select('user_id')
        .eq('user_id', userId)
        .single();

      // 如果用户没有归因记录，保存当前的UTM数据
      if (!existingAttribution) {
        await saveUTMAttributionOnRegistration(userId, userEmail);
        console.log('UTM attribution saved for returning user:', userId);
      } else {
        console.log('User already has UTM attribution, skipping save:', userId);
      }
    }

  } catch (error) {
    console.error('Failed to update UTM attribution on login:', error);
  }
}

/**
 * 获取用户的归因报告（从Supabase）
 */
export async function getUserAttributionReport(userId: string) {
  try {
    // 从 Supabase 获取用户归因数据
    const { data: supabaseData, error } = await supabase
      .from('user_utm_attribution')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      console.error('Failed to get user attribution from Supabase:', error);
    }
    
    // 同时返回 PostHog 的实时数据
    const posthogData = getFullAttributionData();
    
    return {
      supabase_data: supabaseData,
      posthog_data: posthogData,
      attribution: supabaseData // 向后兼容
    };
  } catch (error) {
    console.error('Failed to get user attribution report:', error);
    return {
      supabase_data: null,
      posthog_data: getFullAttributionData(),
      attribution: null
    };
  }
}

export type { UTMAttribution };